import deepmerge from 'deepmerge';

import { POSTER_EDGE_MAX_LENGTH } from './constants';
import {
  AspectRatio,
  CombinedPlaylistPlayerOptions,
  VideoPlayerProps,
  VideoPlaylistPlayerProps,
} from './types';

const IMAGE_SIZE_REGEX = /\/(-?\d+x-?\d+)(?=\.[^.]+$)/;

export type PropType = VideoPlayerProps | VideoPlaylistPlayerProps;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function handleMerge<T extends { [key: string]: any }>(
  toMerge: Partial<T>[]
) {
  const [options, ...rest] = toMerge;
  const result = deepmerge.all<T>([options || {}, ...rest]);

  return Object.keys(result).reduce((acc: T, curr: string) => {
    // explicit undefined check
    if (result[curr] === undefined) {
      return {
        ...acc,
        [curr]: options ? options[curr] : {},
      };
    }

    return {
      ...acc,
      [curr]: result[curr],
    };
  }, {} as T);
}

export function generatePlaylistPlayerOptions<T extends PropType>(props: T) {
  const {
    // eslint-disable-next-line camelcase
    startMuted: start_muted,
    foundationLayout: useFoundationLayout = true,
    autoplay = false,

    loadSuggestions,
    preloadThreshold,
    repeat,
    startFrom = 0,
    onReady,
    onError,
    transitionDuration,

    options,
  } = props as VideoPlaylistPlayerProps;

  const itemOptions = handleMerge<CombinedPlaylistPlayerOptions>([
    options,
    {
      autoplay,
      // eslint-disable-next-line camelcase
      start_muted,
      useFoundationLayout,
    },
  ]);
  const playlistOption = {
    loadSuggestions,
    preloadThreshold,
    repeat,
    startFrom,
    onReady,
    onError,
    transitionDuration,
  };

  return { itemOptions, playlistOption };
}

export function generatePlayerOptions<T extends PropType>(props: T) {
  const {
    autoplay = false,
    options,
    // eslint-disable-next-line camelcase
    startMuted: start_muted,
    foundationLayout: useFoundationLayout = true,
    onReady,
    onError,
  } = props as VideoPlayerProps;

  const result = handleMerge([
    options,
    {
      autoplay,
      // eslint-disable-next-line camelcase
      start_muted,
      useFoundationLayout,
    },
  ]);

  return {
    options: result,
    callbacks: {
      onReady,
      onError,
    },
  };
}

export function parseAspectRatio(aspectRatio: string): AspectRatio {
  const [widthStr = '1', heightStr = '1'] = aspectRatio.split(':');
  let width = parseInt(widthStr, 10);
  let height = parseInt(heightStr, 10);

  if (isNaN(width)) {
    width = 1;
  }

  if (isNaN(height)) {
    height = 1;
  }

  return { width, height };
}

export function getPosterImageUrl(imageUrl: string, aspectRatio: AspectRatio) {
  let width = POSTER_EDGE_MAX_LENGTH;
  let height = POSTER_EDGE_MAX_LENGTH;

  if (aspectRatio.width > aspectRatio.height) {
    height = Math.round((aspectRatio.height / aspectRatio.width) * width);
  } else {
    width = Math.round((aspectRatio.width / aspectRatio.height) * height);
  }

  return imageUrl.replace(IMAGE_SIZE_REGEX, `/${width}x${height}`);
}
